import { createContext, useContext, useState, useEffect } from 'react'
import {
  onAuthChange,
  signInWithEmail,
  signUpWithEmail,
  signInWithGoogle,
  signInWithApple,
  signOut,
  deleteAccount as firebaseDeleteAccount,
  handleRedirectResult
} from '../services/firebase'
import { syncUserProfile, deleteUserData } from '../services/firestore'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  // Handle redirect result on mount (for Google/Apple sign-in)
  useEffect(() => {
    const checkRedirectResult = async () => {
      console.log('[AUTH] Checking for OAuth redirect result...')
      const result = await handleRedirectResult()
      if (result.error) {
        console.error('[AUTH] Redirect error:', result.error)
      } else if (result.user) {
        console.log('[AUTH] Redirect sign-in successful:', result.user.email)
      }
    }
    checkRedirectResult()
  }, [])

  useEffect(() => {
    console.log('[AUTH] Setting up onAuthChange listener...')
    const unsubscribe = onAuthChange(async (firebaseUser) => {
      console.log('[AUTH] ===== AUTH STATE CHANGE =====')
      console.log('[AUTH] firebaseUser:', firebaseUser)
      console.log('[AUTH] firebaseUser email:', firebaseUser?.email)
      console.log('[AUTH] firebaseUser uid:', firebaseUser?.uid)
      setUser(firebaseUser)

      if (firebaseUser) {
        // Sync with Supabase
        console.log('[AUTH] Syncing user profile with Supabase...')
        const userProfile = await syncUserProfile(firebaseUser)
        console.log('[AUTH] Profile synced:', userProfile?.id)
        setProfile(userProfile)
      } else {
        console.log('[AUTH] Clearing profile (no user)')
        setProfile(null)
      }

      setLoading(false)
    })

    return () => unsubscribe()
  }, [])

  const login = async (email, password) => {
    const result = await signInWithEmail(email, password)
    return result
  }

  const signup = async (email, password, displayName) => {
    const result = await signUpWithEmail(email, password, displayName)
    return result
  }

  const loginWithGoogle = async () => {
    const result = await signInWithGoogle()
    return result
  }

  const loginWithApple = async () => {
    const result = await signInWithApple()
    return result
  }

  const logout = async () => {
    console.log('[AUTH] Logout initiated')
    const result = await signOut()
    if (!result.error) {
      console.log('[AUTH] Logout successful, clearing state')
      setUser(null)
      setProfile(null)
    } else {
      console.error('[AUTH] Logout failed:', result.error)
    }
    return result
  }

  const deleteAccount = async () => {
    if (!profile?.id) {
      return { error: 'No user profile found' }
    }

    // First delete user data from Supabase
    const deleteResult = await deleteUserData(profile.id)
    if (deleteResult.error) {
      return { error: deleteResult.error }
    }

    // Then delete Firebase account
    const firebaseResult = await firebaseDeleteAccount()
    if (firebaseResult.error) {
      return { error: firebaseResult.error }
    }

    // Clear local state
    setUser(null)
    setProfile(null)

    return { error: null }
  }

  const isAuthenticated = !!user

  // Debug log when values change
  useEffect(() => {
    console.log('[AUTH] Context value updated:')
    console.log('  - user:', user?.email || null)
    console.log('  - isAuthenticated:', isAuthenticated)
    console.log('  - loading:', loading)
  }, [user, isAuthenticated, loading])

  const value = {
    user,
    profile,
    loading,
    login,
    signup,
    loginWithGoogle,
    loginWithApple,
    logout,
    deleteAccount,
    isAuthenticated
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
